"use client"

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"

import { apiClient } from "@/lib/api/client"
import { apiUrl } from "@/lib/api/url"
import { leadDeliveryKeys } from "@/lib/apps/autoleads/keys/lead-delivery"
import { teamMailingProfileKeys } from "@/lib/apps/autoleads/keys/team-mailing-profile"

import type { TeamMailingProfile } from "@/nango/types/apps.autoleads/autoleads/AutoLeads"
import type {
  ExportColumns,
  ExportColumnsConfig,
} from "@/nango/types/apps.autoleads/autoleads/ExportColumns"

function withTeam(path: string, teamId: number | null): string {
  const url = apiUrl(path)
  return teamId === null ? url : `${url}?team_id=${teamId}`
}

export function useTeamMailingProfile(
  teamId: number | null = null,
  enabled: boolean = true,
) {
  return useQuery({
    queryKey: teamMailingProfileKeys.get(teamId),
    queryFn: () =>
      apiClient<TeamMailingProfile | null>({
        url: withTeam("api/al/team_mailing_profile/get/", teamId),
        method: "GET",
      }),
    enabled,
  })
}

/** The onboarding/tutorial mutations all POST {team_id} and return the profile. */
function useProfileAction(teamId: number | null, action: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () =>
      apiClient<TeamMailingProfile>({
        url: apiUrl(`api/al/team_mailing_profile/${action}/`),
        method: "POST",
        data: { team_id: teamId },
      }),
    onSuccess: (profile) => {
      queryClient.setQueryData<TeamMailingProfile | null>(
        teamMailingProfileKeys.get(teamId),
        (current) => ({ ...profile, products: current?.products ?? [] }),
      )
      // The caller's team id is not always the one the gate reads: the sidebar
      // head can hold a stale team, and the layout falls back to the first team
      // instead. Writing one key would then leave the gate on its old answer,
      // which is what made "Restart tutorial" look dead. Invalidate the rest.
      void queryClient.invalidateQueries({
        queryKey: teamMailingProfileKeys.all,
      })
    },
  })
}

export function useCompleteOnboarding(teamId: number | null = null) {
  return useProfileAction(teamId, "complete_onboarding")
}

export function useCompleteTutorial(teamId: number | null = null) {
  return useProfileAction(teamId, "complete_tutorial")
}

export function useResetTutorial(teamId: number | null = null) {
  return useProfileAction(teamId, "reset_tutorial")
}

const EXPORT_SETTINGS_PATH = "api/al/team_mailing_profile/export_settings/"

// The columns the team's files can carry, and its current setup.
export function useTeamExportSettings(
  teamId: number | undefined,
  enabled: boolean = true,
) {
  return useQuery({
    queryKey: teamMailingProfileKeys.exportSettings(teamId ?? 0),
    queryFn: () =>
      apiClient<ExportColumns>({
        url: withTeam(EXPORT_SETTINGS_PATH, teamId ?? null),
        method: "GET",
      }),
    enabled: enabled && teamId !== undefined,
  })
}

export function useSaveTeamExportSettings(
  teamId: number,
  options: { successMessage?: string; onSuccess?: () => void } = {},
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (config: ExportColumnsConfig) =>
      apiClient<ExportColumns>({
        url: apiUrl(EXPORT_SETTINGS_PATH),
        method: "POST",
        data: { ...config, team_id: teamId },
      }),
    onSuccess: (data) => {
      queryClient.setQueryData(
        teamMailingProfileKeys.exportSettings(teamId),
        data,
      )
      // Delivery previews show the same headers and rows the files carry.
      void queryClient.invalidateQueries({ queryKey: leadDeliveryKeys.all })
      if (options.successMessage) toast.success(options.successMessage)
      options.onSuccess?.()
    },
    onError: (err) => toast.error(err.message),
  })
}
